import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon, SparklesIcon, XIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import type { Condition } from '../data/loanData';
type DocumentPreviewModalProps = {
  condition: Condition | null;
  onClose: () => void;
  onAccept: () => void;
  onUnassociate: () => void;
};
const zoomLevels = [0.85, 1, 1.25] as const;
function confidenceTone(score: number): string {
  if (score >= 0.85) return 'text-success';
  if (score >= 0.6) return 'text-amber-600';
  return 'text-danger';
}
function DocumentPage({
  condition,
  page
}: {
  condition: Condition;
  page: number;
}) {
  const preview = condition.preview;
  return <article aria-label={`${condition.document} page ${page}`} className="mx-auto w-[520px] rounded-sm bg-white p-8 shadow-lg ring-1 ring-gray-200" data-id="element-170" data-mp="1b83sazvc1iem">
      <header className="border-b border-gray-300 pb-4" data-id="element-171" data-mp="23x118cdnm2up">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400" data-id="element-172" data-mp="z5ewzpwrav0m">
          {preview?.docType}
        </p>
        <h3 className="mt-1 text-[15px] font-bold text-ink" data-id="element-173" data-mp="1368mgnpukhvm">{preview?.issuer}</h3>
        <p className="mt-0.5 text-[11.5px] text-gray-500" data-id="element-174" data-mp="pgwxdc07qtyk">{preview?.docDate}</p>
      </header>

      {page === 1 ? <dl className="mt-5 space-y-2.5" data-id="element-175" data-mp="udx4lq8su2r9">
          {preview?.fields.map(field => <div key={field.label} className={['flex items-baseline gap-3 rounded px-2 py-1.5', field.highlight ? 'bg-brand-soft ring-1 ring-brand/30' : ''].join(' ')} data-id="element-176" data-mp="fgwh5yxn4tyl">
              <dt className="w-40 shrink-0 text-[11px] uppercase tracking-wide text-gray-500" data-id="element-177" data-mp="bg2pqvkzox0y">
                {field.label}
              </dt>
              <dd className="text-[12.5px] font-medium text-ink" data-id="element-178" data-mp="20wlma8x12320">{field.value}</dd>
            </div>)}
        </dl> : <p className="mt-5 text-[11.5px] font-medium uppercase tracking-wide text-gray-400" data-id="element-179" data-mp="25s10j1yy60yy">
          Continued — page {page}
        </p>}

      <div className="mt-6 space-y-2" aria-hidden="true" data-id="element-180" data-mp="1zd256146du2a">
        {Array.from({
        length: page === 1 ? 8 : 14
      }).map((_, index) => <div key={index} className="h-2 rounded bg-gray-100" style={{
        width: `${[100, 92, 96, 74, 88, 98, 66, 90, 82, 94, 70, 86, 96, 78][index % 14]}%`
      }} data-id="element-181" data-mp="1ufad973j67y3" data-mpi={index} />)}
      </div>

      <footer className="mt-8 flex items-center justify-between border-t border-gray-200 pt-3 text-[10px] text-gray-400" data-id="element-182" data-mp="2i4mjby3k9zw6">
        <span className="truncate" data-id="element-183" data-mp="2ddd0bgciput9">{condition.document}</span>
        <span data-id="element-184" data-mp="2dbjoznqymmex">
          Page {page} of {preview?.pages ?? 1}
        </span>
      </footer>
    </article>;
}
export function DocumentPreviewModal({
  condition,
  onClose,
  onAccept,
  onUnassociate
}: DocumentPreviewModalProps) {
  const [page, setPage] = useState(1);
  const [zoomIndex, setZoomIndex] = useState(1);
  useEffect(() => {
    if (condition) {
      setPage(1);
      setZoomIndex(1);
    }
  }, [condition]);
  useEffect(() => {
    if (!condition) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [condition, onClose]);
  const pages = condition?.preview?.pages ?? 1;
  return <AnimatePresence data-id="element-185" data-mp="3rcpvdjvmy838">
      {condition && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-186" data-mp="76iwelt24so">
          <motion.button type="button" aria-label="Close preview" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="absolute inset-0 h-full w-full cursor-default bg-ink/60" data-id="element-187" data-mp="4ttjgdpws5l" />
          <motion.div role="dialog" aria-modal="true" aria-label={`Preview ${condition.document}`} initial={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 420,
        damping: 34
      }} className="relative flex max-h-[92vh] w-full max-w-[940px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl" data-id="element-188" data-mp="iyh9svrj6hkk">
            <header className="flex items-start gap-3 border-b border-gray-200 px-5 py-3.5" data-id="element-189" data-mp="xnyg3n224fd0">
              <div className="min-w-0" data-id="element-190" data-mp="sq6eu2fivgw5">
                <h2 className="truncate text-[15px] font-bold text-ink" data-id="element-191" data-mp="16od3qt2hu6jo">
                  {condition.document}
                </h2>
                <p className="mt-0.5 truncate text-[12px] text-gray-500" data-id="element-192" data-mp="11o8laxi0z1qt">
                  {condition.source} · matched to “{condition.name}”
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100" data-id="element-193" data-mp="2d4ylnzzrozci">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-194" data-mp="2pom33b9vk7zl" />
              </button>
            </header>

            <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-195" data-mp="2bp48gf9we0ib">
              <div className="flex min-h-0 flex-col border-r border-gray-200" data-id="element-196" data-mp="2gmmmul6op7xp">
                <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2" data-id="element-197" data-mp="34d9tz48rztj7">
                  <button type="button" onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1} aria-label="Previous page" className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40" data-id="element-198" data-mp="39aifnet4ufp7">
                    <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-199" data-mp="3s2b5x1gthmt8" />
                  </button>
                  <span className="text-[12px] text-gray-600" data-id="element-200" data-mp="5rm0xypnq85u">
                    Page {page} of {pages}
                  </span>
                  <button type="button" onClick={() => setPage(prev => Math.min(pages, prev + 1))} disabled={page === pages} aria-label="Next page" className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40" data-id="element-201" data-mp="r7xyv2wxoeo">
                    <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-202" data-mp="398ue459fwju8" />
                  </button>

                  <div className="ml-auto flex items-center gap-2" data-id="element-203" data-mp="34a1b56rs4b8b">
                    <button type="button" onClick={() => setZoomIndex(prev => Math.max(0, prev - 1))} disabled={zoomIndex === 0} aria-label="Zoom out" className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40" data-id="element-204" data-mp="347ol06bvauk6">
                      <ZoomOutIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-205" data-mp="1fryeqo5y3qux" />
                    </button>
                    <span className="text-[12px] text-gray-600" data-id="element-206" data-mp="1oqtqsgvo7v65">
                      {Math.round(zoomLevels[zoomIndex] * 100)}%
                    </span>
                    <button type="button" onClick={() => setZoomIndex(prev => Math.min(zoomLevels.length - 1, prev + 1))} disabled={zoomIndex === zoomLevels.length - 1} aria-label="Zoom in" className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40" data-id="element-207" data-mp="s246kjwkmqh0">
                      <ZoomInIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-208" data-mp="da2730yexzhi" />
                    </button>
                    <button type="button" aria-label="Download document" className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-600 transition-colors hover:bg-gray-50" data-id="element-209" data-mp="32tysjo6pvns1">
                      <DownloadIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-210" data-mp="37s0b8808dl2z" />
                    </button>
                  </div>
                </div>

                <div className="scrollbar-thin min-h-0 flex-1 overflow-auto bg-gray-100 p-6" data-id="element-211" data-mp="3gthn1e6lv0lv">
                  <div style={{
                transform: `scale(${zoomLevels[zoomIndex]})`,
                transformOrigin: 'top center'
              }} data-id="element-212" data-mp="3vie5m56afw0q">
                    <DocumentPage condition={condition} page={page} data-id="element-213" data-mp="8depi197isb9" />
                  </div>
                </div>
              </div>

              <aside className="scrollbar-thin min-h-0 overflow-y-auto px-4 py-4" data-id="element-214" data-mp="3fwf06fjk515">
                <div className="rounded-xl border border-brand/30 bg-brand-soft/60 p-3.5" data-id="element-215" data-mp="n03m9vc2qrdy">
                  <div className="flex items-center gap-2" data-id="element-216" data-mp="rzq28cjaelqg">
                    <SparklesIcon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-217" data-mp="rx3uocwec688" />
                    <p className="text-[12.5px] font-semibold text-ink" data-id="element-218" data-mp="e1rqlewcp3en">AI match</p>
                    {typeof condition.confidence === 'number' && <span className={['ml-auto text-[13px] font-bold', confidenceTone(condition.confidence)].join(' ')} data-id="element-219" data-mp="49lxu2y9yhye">
                        {Math.round(condition.confidence * 100)}%
                      </span>}
                  </div>
                  <p className="mt-2 text-[12px] leading-snug text-gray-600" data-id="element-220" data-mp="3hfvvabvmxx6k">
                    {condition.reasoning}
                  </p>
                  <p className="mt-2 text-[11px] text-gray-500" data-id="element-221" data-mp="3mez2a1v6edh6">
                    Confidence reflects match to the condition type, not whether the
                    condition is satisfied.
                  </p>
                </div>

                <section className="mt-4" data-id="element-222" data-mp="1f22gh93m31wi">
                  <h3 className="text-[12px] font-semibold text-ink" data-id="element-223" data-mp="1f3dtlziy5m0a">Extracted details</h3>
                  <dl className="mt-2 space-y-2" data-id="element-224" data-mp="1k26wyz4irwmq">
                    {condition.preview?.fields.map(field => <div key={field.label} data-id="element-225" data-mp="27r9ocyoyh3uf">
                        <dt className="text-[11px] uppercase tracking-wide text-gray-400" data-id="element-226" data-mp="2bsbm1b3857ua">
                          {field.label}
                        </dt>
                        <dd className="text-[12.5px] text-ink" data-id="element-227" data-mp="1y0og0jjhckyb">{field.value}</dd>
                      </div>)}
                  </dl>
                </section>

                <section className="mt-4" data-id="element-228" data-mp="2qokjq87gvd4l">
                  <h3 className="text-[12px] font-semibold text-ink" data-id="element-229" data-mp="2qnra8zh4i9ir">Condition</h3>
                  <p className="mt-1 text-[12.5px] text-gray-600" data-id="element-230" data-mp="2vhn9e594i2fi">{condition.name}</p>
                  <p className="mt-0.5 text-[11.5px] text-gray-400" data-id="element-231" data-mp="34tkyoap2y7x6">{condition.category}</p>
                </section>
              </aside>
            </div>

            <footer className="flex flex-wrap items-center gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3" data-id="element-232" data-mp="3elgx5mv2w01x">
              <p className="mr-auto text-[12px] text-gray-500" data-id="element-233" data-mp="39lkrzkana2ks">
                Does this document satisfy the condition?
              </p>
              <button type="button" onClick={() => {
            onUnassociate();
            onClose();
          }} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12.5px] font-medium text-ink transition-colors hover:bg-white/60" data-id="element-234" data-mp="2lwjng84xnbks">
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-235" data-mp="b35t38dz3lxm" />
                Unassociate
              </button>
              <button type="button" onClick={() => {
            onAccept();
            onClose();
          }} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-dark" data-id="element-236" data-mp="64m1arcp7fxc">
                <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-237" data-mp="s5clke5gjb7n" />
                Accept match
              </button>
            </footer>
          </motion.div>
        </div>}
    </AnimatePresence>;
}